var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
import ChannelContext from '@jetshop/core/components/ChannelContext';
import t from '@jetshop/intl';
import { withTheme } from 'emotion-theming';
import get from 'lodash.get';
import * as React from 'react';
import styled, { css } from 'react-emotion';
import { useInView } from 'react-intersection-observer';
import { getFullUrl } from './utils/getFullUrl';
import { getImageWidths } from './utils/getImageWidths';
import { getLargestSize } from './utils/getLargestSize';
import { getLQIP } from './utils/getLQIP';
import { getSizesMap } from './utils/getSizesMap';
import { getSrcSetFromWidths } from './utils/getSrcSetFromWidths';
import { paddingForAspectRatio } from './utils/paddingForAspectRatio';
import useWarningInDev from '@jetshop/core/hooks/useWarningInDev';
const baseImageStyles = css `
  width: 100%;
  background-repeat: no-repeat;
  background-position: center center;
  transition: opacity 0.4s linear;
  position: relative;
`;
const ImageErrorDivWrapper = styled('div') `
  ${baseImageStyles}
  background: #e2e2e2;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ImageDivWrapper = styled('div') `
  ${baseImageStyles}
`;
const ImageError = styled('div') `
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;
export const transparentDataImg = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
function useFinalImage({ hasFalsySrc, imageSrc, sizesMap, srcset }) {
    // Use a ref to store the final Src, since it doesn't change after being set
    const [finalSrc, setFinalSrc] = React.useState(null);
    // Set up our IntersectionObserver
    const [ref, inView] = useInView({ triggerOnce: true });
    // Set up our base image state
    const [loaded, setLoaded] = React.useState(false);
    const [error, setError] = React.useState(false);
    // When `inView` changes, run this effect This is the core of the lazy loading
    // — it creates an image and then fires an event to set the `finalSrc` once it
    // has finished loading the image data.
    React.useEffect(() => {
        // Skip if this component is not in the current viewport
        if (!inView)
            return;
        // Skip this effect if the image src is falsy
        if (hasFalsySrc)
            return;
        // Create an image element
        let img = new Image();
        img.srcset = srcset;
        img.sizes = sizesMap;
        img.src = imageSrc;
        function onImageLoad() {
            setFinalSrc(img.currentSrc || img.src);
            setLoaded(true);
            setError(false);
            unsetHandlers();
        }
        function onImageError() {
            setLoaded(false);
            setError(true);
            unsetHandlers();
        }
        // Give garbage collection a hand
        function unsetHandlers() {
            if (img) {
                img.onload = null;
                img.onerror = null;
            }
        }
        function cleanup() {
            unsetHandlers();
            img = null;
        }
        if (img) {
            // Once it's finished loading, save the src in in our ref
            img.onload = onImageLoad;
            // If it fails to load, set error state
            img.onerror = onImageError;
        }
        return cleanup;
    }, [hasFalsySrc, imageSrc, inView, sizesMap, srcset]);
    return { ref, loaded, error, src: finalSrc };
}
const ImageLoader = function ImageLoader(_a) {
    var { src, cover = false, alt = '', crop = false, sizes = [1], fillAvailableSpace = false, gravity, aspect = null, error, theme, children, quality, className } = _a, rest = __rest(_a, ["src", "cover", "alt", "crop", "sizes", "fillAvailableSpace", "gravity", "aspect", "error", "theme", "children", "quality", "className"]);
    // Warn if gravity was provided but crop was not
    useWarningInDev(gravity && !crop, 'You have provided a gravity without setting crop to true. Your image will not be cropped, and the `gravity` prop will do nothing. Please remove the `gravity` prop.');
    // Warn if the src is a falsy value
    useWarningInDev(!!!src, 'You have provided a falsy value to the Image component. If you intentionally want to display a blank Image component, you may import `transparentDataImg` and use it for the src. Otherwise, this is probably a bug.');
    // The baseUrl will change based on the currently-selected channel.
    // We need it to correctly construct the image url
    const { selectedChannel } = React.useContext(ChannelContext);
    const baseUrl = get(selectedChannel, 'imageUrl')
        ? String(selectedChannel.imageUrl)
        : null;
    // For backwards-compatibility (and dev convenience) reasons, we accept a
    // falsy `src` prop on Image. undefined | null will act just like the normal
    // Image component does, except that it will show a transparent image where
    // the image would normally load. This may be useful in cases where the end
    // user is lazy and wants to do this:
    // const { imageSrc } = someDataRequest();
    // <Image src={imageSrc} />
    // Where imageSrc may be undefined | null while the request is occurring. The
    // preferred method is to be more explicit:
    // const {imageSrc, loading} = someDataRequest();
    //<Image src={loading ? transparentDataImg : imageSrc } />
    // Therefore, whilst we will handle a null | undefined src, we will warn
    // against its usage in dev.
    let hasFalsySrc = true;
    let imageSrc = '';
    if (Array.isArray(src)) {
        // maintain support for deprecated image sizes object
        hasFalsySrc = false;
        imageSrc = getFullUrl(getLargestSize(src), baseUrl);
    }
    // When we have a string for the src
    if (!!src && typeof src === 'string') {
        hasFalsySrc = false;
        imageSrc = getFullUrl(src, baseUrl);
    }
    // useMemo so this runs only if the imageSrc changes. This calculates the
    // strings used for `sizes`, `srcset`, and the `lqip`
    const { sizesMap, srcset, lqip, fillAvailableSpaceStyles } = React.useMemo(() => {
        if (hasFalsySrc) {
            return {};
        }
        const widths = getImageWidths(theme.breakpoints, sizes);
        const srcset = getSrcSetFromWidths(imageSrc, widths, aspect, {
            crop,
            gravity,
            quality
        });
        const lqip = getLQIP(imageSrc, widths, baseUrl, aspect);
        const sizesMap = getSizesMap(theme.breakpoints, sizes);
        const fillAvailableSpaceStyles = fillAvailableSpace
            ? {
                backgroundSize: 'cover',
                paddingTop: 0,
                height: '100%'
            }
            : {};
        return { sizesMap, srcset, lqip, fillAvailableSpaceStyles };
    }, [
        aspect,
        baseUrl,
        crop,
        fillAvailableSpace,
        gravity,
        hasFalsySrc,
        imageSrc,
        quality,
        sizes,
        theme.breakpoints
    ]);
    // Creates an `img` DOM element and uses its onload/onerror callbacks to track
    // load state
    const finalSrc = useFinalImage({
        hasFalsySrc,
        imageSrc,
        sizesMap,
        srcset
    });
    // Render nothing if src is boolean
    if (typeof src === 'boolean') {
        return null;
    }
    // Return with the error state if the image could not load
    if (finalSrc.error) {
        // Use the error component passed by props
        if (error)
            return error({});
        // Otherwise use a default one
        return (React.createElement(ImageErrorDivWrapper, Object.assign({ "data-flight-image-root": "", "data-flight-image-loaded": "false", "data-flight-image-error": "true", className: className, style: Object.assign({ paddingTop: aspect ? paddingForAspectRatio(aspect) : '100%' }, fillAvailableSpaceStyles) }, rest),
            React.createElement(ImageError, null, t('Image Not Found')),
            children));
    }
    // If loading or no error…
    // Set the image to a transparent placeholder if the src is undefined | null
    const backgroundImage = hasFalsySrc
        ? transparentDataImg
        : finalSrc.src || lqip;
    const loaded = finalSrc.loaded || hasFalsySrc;
    return (React.createElement(ImageDivWrapper, Object.assign({ innerRef: finalSrc.ref, role: "img", "aria-label": alt, "data-srcset": srcset, "data-flight-image-root": "", "data-flight-image-loaded": loaded, "data-flight-image-error": "false", className: className, style: Object.assign({ backgroundImage: `url("${backgroundImage}")`, opacity: loaded ? 1 : 0.3, backgroundSize: cover ? 'cover' : 'contain', paddingTop: aspect ? paddingForAspectRatio(aspect) : '100%' }, fillAvailableSpaceStyles) }, rest), children));
};
export default withTheme(ImageLoader);
//# sourceMappingURL=Image.js.map